import styled, { css, keyframes } from 'styled-components';
import _ from 'lodash';
import Text from 'components/Text';
import Icon from 'components/Icon';
import {
  colorVariables,
  fontVariables,
  buttonSizeVariables,
  socialsColor,
  socialsActiveColor,
  socialsHoverColor,
} from 'theme/variables';
import base from './base';
import { embedWrapper, embedExpandPrice, embedExpandQuality } from './mediaQueries';

export const ButtonStyled = styled.button`
  ${base};
  // styles depends on buttonStyle prop
  ${(props) =>
    props.buttonStyle === 'background' &&
    css`
      height: 40px;
      color: white;
      background-color: ${colorVariables.blue_smalt};
      border-radius: 3px;
      &:hover {
        background-color: ${!props.isMobile && !props.disable
          ? colorVariables.blue_navyblue
          : colorVariables.blue_smalt};
        transition: 135ms ease-out;
      }
      &:active {
        background-color: ${colorVariables.blue_navyblue};
        transition: 135ms ease-out;
      }
    `}
  ${(props) =>
    props.buttonStyle === 'transparent' &&
    css`
      background-color: transparent;
    `}
  // styles for disabled button
  ${(props) =>
    props.disable &&
    css`
      cursor: default;
      pointer-events: none;
      opacity: 0.75;
    `}
  // styles for loading button
  ${(props) =>
    props.isLoading &&
    css`
      cursor: default;
    `}
  // styled depends on size prop
  ${(props) =>
    props.size &&
    css`
      width: ${_.get(buttonSizeVariables, props.size, null)};
    `};

  ${(props) =>
    !props.isMobile &&
    !props.disable &&
    props.buttonStyle !== 'background' &&
    css`
      &:hover {
        ${StyledIcon} {
          fill: ${_.get(colorVariables, props.hoverColor, colorVariables.blue_navyblue)};
          transition: 135ms ease-out;
        }
        ${StyledText} {
          color: ${_.get(colorVariables, props.hoverColor, colorVariables.blue_navyblue)};
          transition: 135ms ease-out;
        }
      }
    `};

  ${(props) =>
    !props.isMobile &&
    !props.disable &&
    props.buttonStyle === 'transparent' &&
    css`
      &:hover {
        ${StyledIcon} {
          fill: ${!props.isMobile && !props.disable
            ? colorVariables.blue_navyblue
            : _.get(colorVariables, props.color, colorVariables.blue_doger)};
          transition: 135ms ease-out;
        }
        ${StyledText} {
          color: ${() => {
            if (!props.isMobile && !props.disable) {
              return css`
                ${_.get(colorVariables, props.hoverColor, colorVariables.blue_navyblue)};
              `;
            }

            return css`
              ${_.get(colorVariables, props.color, colorVariables.blue_doger)};
            `;
          }};
          transition: 135ms ease-out;
        }
      }
    `};

  ${(props) =>
    props.isMobile &&
    props.buttonStyle !== 'background' &&
    css`
      &:active {
        ${StyledIcon} {
          fill: ${_.get(colorVariables, props.hoverColor, colorVariables.blue_navyblue)};
          transition: 135ms ease-out;
        }
        ${StyledText} {
          color: ${_.get(colorVariables, props.hoverColor, colorVariables.blue_navyblue)};
          transition: 135ms ease-out;
        }
      }
    `};

  ${(props) =>
    props.buttonStyle === 'transparent' &&
    css`
      &:active {
        ${StyledIcon} {
          fill: ${colorVariables.blue_navyblue};
          transition: 135ms ease-out;
        }
        ${StyledText} {
          color: ${_.get(colorVariables, props.hoverColor, colorVariables.blue_navyblue)};
          transition: 135ms ease-out;
        }
      }
    `};

  ${(props) =>
    props.disable &&
    css`
      &:active {
        ${StyledIcon} {
          fill: ${colorVariables.gray_darkgray};
          cursor: default;
        }
        ${ButtonStyled} {
          color: ${_.get(colorVariables, props.color, colorVariables.blue_doger)};
          cursor: default;
        }
      }
    `};

  ${embedWrapper};
`;

export const SocialWrapper = styled.button`
  ${base};
  flex-direction: column;
  align-items: center;
  width: 48px;
  height: 40px;
  border-radius: 3px;
  transition: background-color 135ms ease-in, color 135ms ease-in;
  &:hover {
    transition: background-color 135ms ease-out, color 135ms ease-out;
  }
  ${(props) =>
    props.iconType &&
    css`
      background-color: ${_.get(socialsColor, props.iconType, null)};
      &:hover {
        background-color: ${_.get(socialsHoverColor, props.iconType, null)};
      }
      &:active {
        background-color: ${_.get(socialsActiveColor, props.iconType, null)};
      }
    `};
`;

export const ContentWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  // styled depends on align props
  ${(props) =>
    props.align === 'left' &&
    css`
      justify-content: flex-start;
    `};
`;

export const StyledText = styled(Text)`
  transform: translate(0, 1px);
  // styles depends on buttonStyle prop
  ${(props) =>
    props.buttonStyle === 'background' &&
    css`
      transform: translate(0, 0);
      color: white;
      font-size: ${fontVariables.fs_md};
    `};
  ${(props) =>
    props.buttonStyle === 'transparent' &&
    css`
      color: ${_.get(colorVariables, props.color, colorVariables.blue_doger)};
    `};
`;

export const StyledIcon = styled(Icon)`
  // hover only when not disabled or mobile
  ${(props) =>
    props.separation &&
    css`
      margin-right: ${props.separation}px;
    `};

  ${SocialWrapper}:hover & {
    fill: white;
  }
  // styles depends on buttonStyle prop
  ${(props) =>
    props.buttonStyle === 'background' &&
    css`
      margin-right: ${props.separation ? `${props.separation}px` : '8px'};
      fill: white;
    `};
  ${(props) =>
    props.buttonStyle === 'transparent' &&
    css`
      fill: ${_.get(colorVariables, props.color, colorVariables.blue_doger)};
    `};
  // styles for disabled button
  ${(props) =>
    props.disable &&
    css`
      fill: ${colorVariables.gray_darkgray};
      cursor: default;
    `};
  // styles for active button
  ${(props) =>
    props.active &&
    css`
      fill: ${colorVariables.blue_navyblue};
    `};
`;

export const StyledExpandedQuality = styled.span`
  display: none;
  margin-left: 5px;
  ${embedExpandQuality};
`;

export const StyledExpandedPrice = styled.span`
  display: none;
  text-transform: lowercase;
  margin-left: 5px;
  ${embedExpandPrice};
`;

const Rotate = keyframes`
  from {
    transform: rotate(-42deg);
  }
  to {
    transform: rotate(318deg);
  }
`;

export const Loader = styled.svg`
  position: relative;
  width: 22px;
  height: 22px;
  transform-origin: center center;
  transform: rotate(-42deg);
  animation: ${Rotate} 915ms linear infinite;
`;

export const LoaderCircle = styled.circle`
  stroke-dasharray: 1, 200;
  stroke-dashoffset: 0;
  stroke-linecap: round;
  stroke: white;
  stroke-width: 3px;
`;
